'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useMutation } from '@apollo/client/react';

import { isValidInsightId } from '@/shared-utilities';
import { UPDATE_INSIGHT_RECORDING } from '@/survey-graphql';

import { stateManager } from './project-usability-recording-state-manager';

/* -------------------------------------------------------------------------- */
/* Recording Duration Updates                                                 */
/* -------------------------------------------------------------------------- */

interface UseRecordingDurationUpdatesOptions {
  insightId: string | null;
  isRecording: boolean;
}

/**
 * Hook to track and update recording duration based on confirmed upload timestamps.
 * Duration is calculated as the time elapsed between recording start and the last
 * confirmed upload, ensuring backend always has the duration of what's actually
 * uploaded, even if network is slow.
 */
export function useRecordingDurationUpdates({
  insightId,
  isRecording,
}: UseRecordingDurationUpdatesOptions) {
  const [updateInsightRecording] = useMutation(UPDATE_INSIGHT_RECORDING);
  const lastSentDurationRef = useRef<number>(0);

  // Send duration update via GraphQL mutation
  const sendDurationUpdate = useCallback(
    async (duration: number, force = false) => {
      // Only send if insightId is available
      if (!insightId || !isValidInsightId(insightId)) {
        return;
      }

      const roundedDuration = Math.floor(duration);

      // Only send if duration has changed (avoid unnecessary API calls) or forced
      if (
        (roundedDuration !== lastSentDurationRef.current &&
          roundedDuration > 0) ||
        force
      ) {
        try {
          await updateInsightRecording({
            variables: {
              input: {
                insightId,
                duration: roundedDuration,
              },
            },
          });
          lastSentDurationRef.current = roundedDuration;
        } catch {
          // Silently fail - we'll retry on next update
          // Don't interrupt recording for duration update failures
        }
      }
    },
    [insightId, updateInsightRecording]
  );

  // Handle chunk upload completion - increment confirmed chunk count and send duration
  // Note: We use a ref to check isRecording at call time, not at callback creation time
  const isRecordingRef = useRef(isRecording);
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  const handleChunkUploaded = () => {
    // Always increment confirmed chunk count - needed for accurate final duration
    stateManager.confirmChunkUploaded();

    // Only send duration updates while recording is active
    // During finalization, we just track chunks - final duration is sent by updateFinalDuration
    if (!isRecordingRef.current) {
      return;
    }

    // Get confirmed duration based on number of uploaded chunks × chunk duration
    const confirmedDuration = stateManager.getConfirmedDuration();
    if (confirmedDuration > 0) {
      void sendDurationUpdate(confirmedDuration);
    }
  };

  // Reset tracker when recording starts
  const resetTracker = () => {
    lastSentDurationRef.current = 0;
  };

  // Periodic duration updates (every 5 seconds)
  // Only send duration based on confirmed uploads - never wall-clock time
  useEffect(() => {
    if (!isRecording || !insightId || !isValidInsightId(insightId)) {
      return;
    }

    const interval = setInterval(() => {
      const confirmedDuration = stateManager.getConfirmedDuration();
      if (confirmedDuration > 0) {
        void sendDurationUpdate(confirmedDuration);
      }
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [isRecording, insightId, sendDurationUpdate]);

  // Final duration update - ALWAYS use confirmed upload duration
  // Wall-clock time is unreliable because chunks may not have been uploaded
  const updateFinalDuration = async (finalDuration: number | null) => {
    if (!insightId || !isValidInsightId(insightId)) {
      return;
    }

    // CRITICAL: Use confirmed duration (chunks actually uploaded to GCS)
    // Wall-clock duration is misleading - it doesn't reflect what's actually stored
    // The only source of truth is the number of chunks confirmed uploaded × chunk duration
    const confirmedDuration = stateManager.getConfirmedDuration();
    const exactDuration = finalDuration ? Math.floor(finalDuration) : 0;
    const uploadProgress = stateManager.getUploadProgress();

    // Determine if upload was successful (all chunks uploaded)
    // uploadProgress === 100 means all generated chunks were uploaded
    const uploadSuccessful = uploadProgress === 100 || uploadProgress === null;

    let durationToSend: number;
    if (confirmedDuration === 0) {
      // No chunks confirmed - use wall-clock as fallback
      durationToSend = exactDuration;
    } else if (uploadSuccessful && exactDuration > 0) {
      // Upload was successful - use exact duration
      // This is the accurate value; confirmedDuration is just an approximation
      // (last chunk is often partial, so chunks × 4 sec overcounts)
      durationToSend = exactDuration;
    } else if (exactDuration > 0 && exactDuration <= confirmedDuration) {
      // Exact is less than confirmed - use exact (accounts for partial last chunk)
      durationToSend = exactDuration;
    } else {
      // Data loss occurred - use confirmed (what's actually in GCS)
      durationToSend = confirmedDuration;
    }

    if (durationToSend === 0) {
      return; // No duration to send
    }

    // Always send final duration, even if it's the same
    // This ensures backend has the correct final duration
    try {
      await updateInsightRecording({
        variables: {
          input: {
            insightId,
            duration: durationToSend,
          },
        },
      });
      lastSentDurationRef.current = durationToSend;
    } catch {
      // Silently fail - best effort
    }
  };

  return {
    handleChunkUploaded,
    resetTracker,
    updateFinalDuration,
  };
}
